/**
 * MOCK — moteur de développement (DEV-ONLY, ADR 0003 §3).
 *
 * Reproduit la mécanique du proto v4 de façon déterministe à partir de (baseline, params, programme, financing,
 * choices, seed) pour que chaque écran de l'UI s'affiche et se teste tant que `engine/src` (BACKLOG C1) n'est pas
 * livré. Il n'a AUCUNE prétention de calibration : tout coefficient vient de `ctx.params` (fixtures « TODO ») et
 * tout niveau initial de `ctx.baseline`. Les conventions de clés KPI / compte / choix sont celles documentées dans
 * `engine-adapter.ts`. À supprimer quand le moteur réel passera `realEngineReady()`.
 */
import { createRng } from "../../../engine/src/rng.js";
import type {
  Baseline,
  Choices,
  Financing,
  GameEvent,
  LedgerLine,
  Measure,
  MeasureEffects,
  ParamSet,
  Profile,
  Programme,
  Timeline,
  YearState,
} from "../../../engine/src/types.js";
import type { EngineApi, EngineContext, FinancingPreview, TrajectoryPoint, Verdict } from "../engine-adapter.js";
import { bn, num, pct, signed } from "../format.js";
import { FIXTURE_HYP_SEMANTICS } from "./fixtures.js";

const clamp = (x: number, a: number, b: number) => Math.max(a, Math.min(b, x));
const last = (v: Profile) => (Array.isArray(v) ? (v[v.length - 1] ?? 0) : v);
const prof = (v: Profile, k: number, r: number) => (Array.isArray(v) ? (v[Math.min(k, v.length - 1)] ?? 0) : v * r);
const at = (v: Profile, k: number) => (Array.isArray(v) ? (v[k] ?? 0) : k === 0 ? v : 0);

function P(params: ParamSet, id: string): number {
  const v = params[id]?.value;
  if (v === null || v === undefined) throw new Error(`mock-engine: paramètre manquant « ${id} »`);
  return v;
}
function B(b: Baseline, block: "macro" | "apu" | "politics" | "trade", id: string): number {
  const v = b[block][id]?.value;
  if (v === null || v === undefined) throw new Error(`mock-engine: baseline.${block}.${id} manquant`);
  return v;
}
function figVal(f: { value: number | null } | undefined, what: string): number {
  if (!f || f.value === null) throw new Error(`mock-engine: ${what} manquant`);
  return f.value;
}

interface State {
  k: number;
  gdp: number;
  debt: number;
  unemp: number;
  poverty: number;
  co2: number;
  confidence: number;
  eu: number;
  cohesion: number;
  capital: number;
  labour: number;
  gPot: number;
  gPotShift: number;
  apparentRate: number;
  trade: number;
  tradePersist: number;
  lastImpulse: number;
  lostDividend: number;
  ecbDone: boolean;
  overRule: number;
  svc: Record<string, number>;
  fired: Set<string>;
  /** Effets d'options choisies à appliquer à partir de l'année suivante : index = année d'application. */
  queued: Array<{ year: number; opt: GameEvent["options"][number]; title: string }>;
}

function initState(ctx: EngineContext): State {
  const b = ctx.baseline;
  const svc: Record<string, number> = {};
  for (const m of b.state.missions) svc[m.id] = m.service_index?.value ?? 1;
  return {
    k: 0,
    gdp: B(b, "macro", "gdp_nominal"),
    debt: B(b, "apu", "debt_bn"),
    unemp: B(b, "macro", "unemployment_rate"),
    poverty: B(b, "macro", "poverty_rate"),
    co2: B(b, "macro", "ghg_emissions"),
    confidence: B(b, "politics", "confidence"),
    eu: B(b, "politics", "eu_relation"),
    cohesion: B(b, "politics", "cohesion"),
    capital: B(b, "politics", "political_capital"),
    labour: 1,
    gPot: B(b, "macro", "potential_growth"),
    gPotShift: 0,
    apparentRate: B(b, "apu", "apparent_rate"),
    trade: B(b, "trade", "balance_bn"),
    tradePersist: 0,
    lastImpulse: 0,
    lostDividend: 0,
    ecbDone: false,
    overRule: 0,
    svc,
    fired: new Set(),
    queued: [],
  };
}

function measureCost(m: Measure, k: number, hyp: Record<string, number>): number {
  const r = m.ramp > 0 ? clamp((k + 1) / m.ramp, 0, 1) : 1;
  let c = Array.isArray(m.cost) ? (m.cost[Math.min(k, m.cost.length - 1)] ?? 0) : m.cost * r;
  if (m.hyp && FIXTURE_HYP_SEMANTICS[m.id] === "leak_pct" && c < 0) c *= 1 - (hyp[m.id] ?? m.hyp.default) / 100;
  return c;
}
function regimeCost(m: Measure, hyp: Record<string, number>): number {
  let c = last(m.cost);
  if (m.hyp && FIXTURE_HYP_SEMANTICS[m.id] === "leak_pct" && c < 0) c *= 1 - (hyp[m.id] ?? m.hyp.default) / 100;
  return c;
}

interface Fin {
  raw: number;
  stat: number;
  drag: number;
  pol: number;
  pov: number;
  co2: number;
  rabotPct: number;
}
function financingEffects(ctx: EngineContext, fin: Financing): Fin {
  const p = ctx.params;
  const out: Fin = { raw: 0, stat: 0, drag: 0, pol: 0, pov: 0, co2: 0, rabotPct: fin.across_the_board_cut * 100 };
  for (const lv of ctx.levers) {
    const x = fin.revenue_raises[lv.id] ?? 0;
    if (!x) continue;
    const leak = clamp(figVal(lv.leak, `levers.${lv.id}.leak`) * Math.abs(x), 0, P(p, "leak_cap"));
    out.stat += x;
    out.raw += x > 0 ? x * (1 - leak) : x;
    out.drag += P(p, "mult_revenue") * x;
    out.pol += x * figVal(lv.political_cost_per_bn, `levers.${lv.id}.political_cost_per_bn`);
    if (lv.fx?.pov && x > 0) out.pov += last(lv.fx.pov) * x;
    if (lv.fx?.co2 && x > 0) out.co2 += last(lv.fx.co2) * x;
  }
  const rb = out.rabotPct;
  const rabotBn = rb * P(p, "rabot_bn_per_pct");
  out.stat += rabotBn;
  out.raw += rabotBn * (1 - P(p, "rabot_diminishing") * rb);
  out.drag += rabotBn * P(p, "rabot_drag_mult");
  out.pol += rb * P(p, "rabot_political_cost_per_pct");
  return out;
}

function programmePoliticalCost(ctx: EngineContext, programme: Programme): number {
  const share = P(ctx.params, "measure_popular_share");
  return programme.measure_ids.reduce((a, id) => {
    const m = ctx.measures.find((x) => x.id === id);
    if (!m) return a;
    return a + (m.political_cost > 0 ? m.political_cost : m.political_cost * share);
  }, 0);
}

function evalCondition(cond: string, vars: (name: string) => number): boolean {
  return cond.split("&&").every((clause) => {
    const mt = /^\s*([a-z_][a-z0-9_.]*)\s*(<=|>=|<|>|==)\s*(-?\d+(?:\.\d+)?)\s*$/.exec(clause);
    if (!mt) return false;
    const [, name, op, numStr] = mt;
    const lhs = vars(name!);
    const rhs = Number(numStr);
    switch (op) {
      case "<": return lhs < rhs;
      case "<=": return lhs <= rhs;
      case ">": return lhs > rhs;
      case ">=": return lhs >= rhs;
      default: return lhs === rhs;
    }
  });
}

interface SimOpts {
  withEvents: boolean;
  years: number;
}

function simulateCore(ctx: EngineContext, programme: Programme, financing: Financing, choices: Choices, seed: number, opts: SimOpts): Timeline {
  const b = ctx.baseline;
  const p = ctx.params;
  const rng = createRng(seed);
  const s = initState(ctx);
  const baseYear = b.meta.base_year;
  const measures = programme.measure_ids.map((id) => ctx.measures.find((m) => m.id === id)).filter((m): m is Measure => !!m);
  const hyp = programme.hyp;
  const gPot0 = B(b, "macro", "potential_growth");
  const infl0 = B(b, "macro", "inflation");
  const unemp0 = s.unemp;
  const pov0 = s.poverty;
  const oat10 = B(b, "apu", "marginal_rate_oat10");
  const maturity = B(b, "apu", "average_maturity_years");
  const regen = B(b, "politics", "regeneration");
  const tradeBase = s.trade;
  const missionsTotal = b.state.missions.reduce((a, m) => a + figVal(m.credits, `mission ${m.id}`), 0);
  const revenuesTotal = b.state.revenues.reduce((a, r) => a + figVal(r.amount, `revenue ${r.id}`), 0);
  const interest0 = figVal(b.state.debt_interest, "state.debt_interest");
  const balance0 = figVal(b.state.balance, "state.balance");
  const other0 = revenuesTotal - balance0 - missionsTotal - interest0;
  const assoTrend = (k: number) => figVal(b.asso.trend[Math.min(k, b.asso.trend.length - 1)] ?? b.asso.balance, "asso.trend");
  const apulTrend = (k: number) => figVal(b.apul.trend[Math.min(k, b.apul.trend.length - 1)] ?? b.apul.balance, "apul.trend");
  const rule = P(p, "deficit_rule_pct_gdp");
  const fin = financingEffects(ctx, financing);

  const years: YearState[] = [];
  let gameOver: Timeline["game_over"] = null;

  // Vote du budget : coût politique du programme, censure possible.
  s.capital -= programmePoliticalCost(ctx, programme);
  if (opts.withEvents && s.capital - fin.pol < 0 && rng.next() < P(p, "censure_probability")) {
    gameOver = { year: baseYear + 1, reason: "Budget rejeté : capital politique épuisé au vote. Le gouvernement tombe." };
    return { base_year: baseYear, years: [], game_over: gameOver };
  }

  for (let k = 0; k < opts.years; k++) {
    s.k = k;
    const year = baseYear + 1 + k;
    const journal: string[] = [];
    const gdp0 = s.gdp;

    // --- effets des options choisies les années précédentes (appliqués à partir de l'année suivant l'événement)
    let extraSpend = 0;
    let shockG = 0;
    let shockTrade = 0;
    const evFx = { u: 0, pov: 0, coh: 0, conf: 0, eu: 0, lab: 0 };
    for (const q of s.queued) {
      const off = k - q.year;
      if (off < 0) continue;
      extraSpend += at(q.opt.cost, off);
      const f = q.opt.fx;
      if (f.g !== undefined) {
        if (Array.isArray(f.g)) shockG += f.g[off] ?? 0;
        else if (off === 0) s.gPotShift += f.g;
      }
      if (f.trade !== undefined) shockTrade += at(f.trade, off);
      if (off === 0) {
        if (f.u !== undefined) evFx.u += Array.isArray(f.u) ? (f.u[0] ?? 0) : f.u;
        if (f.pov !== undefined) evFx.pov += last(f.pov);
        if (f.coh !== undefined) evFx.coh += last(f.coh);
        if (f.conf !== undefined) evFx.conf += last(f.conf);
        if (f.eu !== undefined) evFx.eu += last(f.eu);
        if (f.lab !== undefined) evFx.lab += last(f.lab);
        if (f.svc) for (const [mid, v] of Object.entries(f.svc)) if (s.svc[mid] !== undefined) s.svc[mid]! += last(v);
        s.capital -= q.opt.political_cost;
      } else if (Array.isArray(f.u)) evFx.u += f.u[off] ?? 0;
    }

    // --- mesures : coûts et canaux
    let mCost = 0, mSpend = 0, mRev = 0, mMult = 0;
    let dTrade = 0, dG = 0, dLab = evFx.lab, dU = evFx.u, dPov = evFx.pov, dCoh = evFx.coh, dConf = evFx.conf, dEu = evFx.eu, dCo2 = 0, dAsso = 0, dApul = 0;
    for (const m of measures) {
      const c = measureCost(m, k, hyp);
      mCost += c;
      if (c > 0) {
        mSpend += c;
        mMult += c * (m.fx.g !== undefined ? P(p, "mult_spend_growth_fx") : P(p, "mult_spend"));
      } else {
        mRev -= c;
        mMult += c * P(p, "mult_revenue");
      }
      const r = m.ramp > 0 ? clamp((k + 1) / m.ramp, 0, 1) : 1;
      const f: MeasureEffects = m.fx;
      if (f.g !== undefined) dG += prof(f.g, k, r);
      if (f.lab !== undefined) dLab += prof(f.lab, k, r);
      if (m.hyp && FIXTURE_HYP_SEMANTICS[m.id] === "labour_pct") dLab -= hyp[m.id] ?? m.hyp.default;
      if (f.u !== undefined) dU += prof(f.u, k, r);
      if (f.pov !== undefined) dPov += prof(f.pov, k, r);
      if (f.coh !== undefined) dCoh += prof(f.coh, k, r);
      if (f.conf !== undefined) dConf += Array.isArray(f.conf) ? prof(f.conf, k, 1) : k === 0 ? f.conf : f.conf * P(p, "confidence_fx_followup");
      if (f.eu !== undefined) dEu += Array.isArray(f.eu) ? prof(f.eu, k, 1) : k === 0 ? f.eu : f.eu * P(p, "eu_fx_followup");
      if (f.co2 !== undefined) dCo2 += prof(f.co2, k, r);
      if (f.asso !== undefined) dAsso += prof(f.asso, k, r);
      if (f.apul !== undefined) dApul += prof(f.apul, k, r);
      if (f.trade !== undefined) dTrade += prof(f.trade, k, r);
      if (f.svc) for (const [mid, v] of Object.entries(f.svc)) if (s.svc[mid] !== undefined) s.svc[mid]! += prof(v, k, 1) * (m.ramp > 1 ? 1 / m.ramp : 1);
      if (m.hyp && FIXTURE_HYP_SEMANTICS[m.id] === "debt_share_pct" && !s.ecbDone) {
        const amt = (s.debt * (hyp[m.id] ?? m.hyp.default)) / 100;
        s.debt -= amt;
        s.ecbDone = true;
        s.lostDividend = (amt * s.apparentRate) / 100;
        journal.push(`Annulation de ${bn(amt, 0)} de dette détenue par l'Eurosystème : le ratio chute, le dividende Banque de France (${bn(s.lostDividend, 0)}/an) disparaît.`);
      }
    }
    if (fin.rabotPct) for (const id of Object.keys(s.svc)) s.svc[id]! -= P(p, "rabot_svc_per_pct") * fin.rabotPct;

    // --- croissance
    s.labour *= 1 + dLab / 100;
    s.gPot = gPot0 + dG + dLab * P(p, "labour_potential_coef") + (s.labour - 1) * P(p, "labour_level_coef") + s.gPotShift;
    const impulse = ((mMult - fin.drag) / gdp0) * 100;
    const confDrag = s.confidence < P(p, "confidence_drag_threshold") ? (P(p, "confidence_drag_threshold") - s.confidence) * P(p, "confidence_drag_coef") : 0;
    const infl = infl0;
    s.tradePersist = s.tradePersist * P(p, "trade_persistence") + shockTrade;
    s.trade = tradeBase + (s.labour - 1) * P(p, "trade_labour_coef") + dTrade + s.tradePersist;
    const tradeG = (P(p, "trade_growth_coef") * (s.trade - tradeBase) / gdp0) * 100;
    const lag = P(p, "impulse_lag_share");
    const g = s.gPot + (1 - lag) * impulse + lag * s.lastImpulse + shockG + tradeG - confDrag;
    s.lastImpulse = impulse;
    const nomG = (1 + g / 100) * (1 + infl / 100) - 1;
    s.gdp = gdp0 * (1 + nomG);
    s.unemp = clamp(s.unemp - P(p, "okun") * (g - gPot0) + dU, P(p, "unemp_floor"), P(p, "unemp_ceiling"));

    // --- comptes de l'État
    const trend = Math.pow(1 + (infl + P(p, "mission_trend_margin")) / 100, k + 1);
    const spendBase = missionsTotal * trend;
    const revTrend = revenuesTotal * (1 + nomG) * Math.pow(1 + P(p, "revenue_trend_growth") / 100, k);
    const revCycl = revTrend * (P(p, "revenue_elasticity_cycle") * (g - gPot0)) / 100 + revTrend * (P(p, "revenue_elasticity_trade") * tradeG) / 100;
    const revEmploy = -(s.unemp - unemp0) * P(p, "revenue_per_unemp_pt");
    const rev = revTrend + revCycl + revEmploy + fin.raw + mRev - s.lostDividend;
    const debtRatioPrev = (s.debt / gdp0) * 100;
    const spread = Math.max(0, P(p, "spread_confidence_ref") - s.confidence) * P(p, "spread_confidence_coef") + Math.max(0, debtRatioPrev - P(p, "spread_debt_ref")) * P(p, "spread_debt_coef");
    const marginal = oat10 + spread;
    s.apparentRate += (marginal - s.apparentRate) / maturity;
    const interest = (s.debt * s.apparentRate) / 100;
    const other = other0 * Math.pow(1 + P(p, "other_spend_growth") / 100, k + 1);
    const stateBal = rev - spendBase - mSpend - interest - other - extraSpend;
    const cycBase = ((g - gPot0) * P(p, "cycl_balance_coef") * s.gdp) / 100;
    const exo = Math.pow(1 + P(p, "exo_trend_growth") / 100, k);
    const asso = assoTrend(k) * exo + cycBase * P(p, "cycl_asso_share") - (s.unemp - unemp0) * P(p, "asso_per_unemp_pt") + dAsso;
    const apul = apulTrend(k) * exo + cycBase * P(p, "cycl_apul_share") + dApul;
    const apuBal = stateBal + asso + apul;
    s.debt -= apuBal;
    const debtRatio = (s.debt / s.gdp) * 100;
    const deficitPct = (apuBal / s.gdp) * 100;

    // --- social, climat, confiance, UE, cohésion, capital
    s.poverty = pov0 + (s.unemp - unemp0) * P(p, "poverty_per_unemp_pt") + dPov + fin.pov;
    s.co2 = s.co2 * (1 - P(p, "co2_trend_decline") / 100 + dCo2 / 100) * (1 + fin.co2);
    const svcIds = Object.keys(s.svc);
    const svcAvg = svcIds.reduce((a, id) => a + s.svc[id]!, 0) / Math.max(1, svcIds.length);
    s.cohesion = clamp(
      s.cohesion + (svcAvg - 1) * P(p, "cohesion_svc_coef") - (s.poverty - pov0) * P(p, "cohesion_poverty_coef") - (s.unemp - unemp0) * P(p, "cohesion_unemp_coef") + dCoh * P(p, "cohesion_fx_share") + P(p, "cohesion_drift") - Math.max(0, s.cohesion - P(p, "cohesion_cap")) * P(p, "cohesion_cap_coef"),
      0,
      100,
    );
    const debtFin = Math.max(0, mCost - fin.raw);
    s.confidence = clamp(
      s.confidence + (deficitPct > -rule ? P(p, "confidence_deficit_bonus") : (deficitPct + rule) * P(p, "confidence_deficit_coef")) - Math.max(0, debtRatio - debtRatioPrev - P(p, "confidence_debt_tolerance")) * P(p, "confidence_debt_coef") - debtFin / P(p, "confidence_debtfin_coef") + dConf + (s.eu < P(p, "confidence_eu_low") ? -P(p, "confidence_eu_penalty") : 0) + P(p, "confidence_drift"),
      0,
      100,
    );
    s.eu = clamp(s.eu + (deficitPct > -rule ? P(p, "eu_deficit_bonus") : -P(p, "eu_deficit_penalty")) + dEu, 0, 100);
    s.overRule = deficitPct < -rule ? s.overRule + 1 : 0;
    const pop = (s.unemp < unemp0 ? P(p, "pop_unemp_bonus") : -P(p, "pop_unemp_penalty")) + (svcAvg - 1) * P(p, "pop_svc_coef") - (s.poverty - pov0) * P(p, "pop_poverty_coef") + (g > P(p, "pop_growth_threshold") ? P(p, "pop_growth_bonus") : 0);
    s.capital = clamp(s.capital - fin.pol + regen + pop, P(p, "capital_floor"), P(p, "capital_ceiling"));

    // --- KPIs, compte, journal
    const kpis: Record<string, number> = {
      deficit_pct_gdp: deficitPct,
      debt_pct_gdp: debtRatio,
      growth_pct: g,
      unemployment_pct: s.unemp,
      poverty_pct: s.poverty,
      confidence: s.confidence,
      cohesion: s.cohesion,
      political_capital: s.capital,
      eu_relation: s.eu,
      co2_mt: s.co2,
      trade_balance_bn: s.trade,
      interest_bn: interest,
      apparent_rate_pct: s.apparentRate,
      marginal_rate_pct: marginal,
      gdp_bn: s.gdp,
      debt_bn: s.debt,
      inflation_pct: infl,
      potential_growth_pct: s.gPot,
      impulse_pt: impulse,
      years_over_deficit_rule: s.overRule,
      labour_index: s.labour,
      balance_state_bn: stateBal,
      balance_asso_bn: asso,
      balance_apul_bn: apul,
      measures_cost_bn: mCost,
      financing_raised_bn: fin.raw,
      debt_financed_bn: debtFin,
      crisis_spend_bn: extraSpend,
      trade_growth_contrib_pt: tradeG,
      spread_pt: spread,
      service_avg: svcAvg,
    };
    const resources: LedgerLine[] = [
      { id: "trend_revenue", label: "Recettes fiscales et non fiscales (tendanciel)", amount: revTrend },
      { id: "cycle", label: `Conjoncture : croissance ${g >= gPot0 ? "au-dessus" : "en dessous"} du potentiel, commerce extérieur`, amount: revCycl },
      { id: "employment", label: `Emploi : cotisations et IR (chômage ${pct(s.unemp)})`, amount: revEmploy },
      { id: "new_revenue", label: "Recettes nouvelles votées (après fuite)", amount: fin.raw },
      { id: "measures_revenue", label: "Mesures qui rapportent", amount: mRev },
      { id: "lost_dividend", label: "Dividende Banque de France perdu", amount: -s.lostDividend },
    ];
    const expenses: LedgerLine[] = [
      { id: "missions", label: "Missions de l'État (tendanciel)", amount: spendBase },
      { id: "measures_spend", label: "Mesures du programme", amount: mSpend },
      { id: "crisis", label: "Décisions de crise", amount: extraSpend },
      { id: "interest", label: `Charge d'intérêts (${pct(s.apparentRate, 2)} apparent, marginal ${pct(marginal, 2)})`, amount: interest },
      { id: "other", label: "Autres dépenses (pensions, UE, dotations)", amount: other },
      { id: "asso_need", label: "Besoin de la Sécurité sociale", amount: -asso },
      { id: "apul_need", label: "Besoin des collectivités", amount: -apul },
    ];
    journal.unshift(
      `Croissance ${pct(g)} (potentiel ${pct(s.gPot)}, impulsion budgétaire ${signed(impulse, 2)} pt) · chômage ${pct(s.unemp)}.`,
      `Mesures : coût net ${bn(mCost)} · financé par recettes/économies ${bn(fin.raw)} · laissé à la dette ${bn(debtFin)}.`,
      `Solde État ${num(stateBal, 0)} Md€ · Sécu ${num(asso, 0)} · collectivités ${num(apul, 0)} → déficit public ${pct(-deficitPct)} du PIB.`,
      `Charge d'intérêts ${bn(interest, 0)} (taux apparent ${pct(s.apparentRate, 2)}, spread ${num(spread, 2)} pt). Dette ${pct(debtRatio, 0)} du PIB.`,
      `Services publics ${num(svcAvg, 2)} · cohésion ${num(s.cohesion, 0)} · confiance ${num(s.confidence, 0)} · UE ${num(s.eu, 0)} · CO₂ ${num(s.co2, 0)} Mt.`,
    );
    if (extraSpend) journal.splice(2, 0, `Décisions de crise : ${bn(extraSpend)} supplémentaires financés par la dette.`);

    // --- événements de l'année
    const fired: string[] = [];
    if (opts.withEvents) {
      const vars = (name: string): number => {
        if (name in kpis) return kpis[name]!;
        if (name.startsWith("service_index.")) return s.svc[name.slice("service_index.".length)] ?? 1;
        if (name.startsWith("measure.")) return programme.measure_ids.includes(name.slice("measure.".length)) ? 1 : 0;
        if (name === "financing.across_the_board_cut") return financing.across_the_board_cut;
        if (name === "year_index") return k;
        return Number.NaN;
      };
      for (const e of ctx.events) {
        if (s.fired.has(e.id)) continue;
        let ok = false;
        if (e.trigger.year !== undefined) ok = e.trigger.year === k + 1;
        else if (e.trigger.condition) ok = evalCondition(e.trigger.condition, vars);
        if (ok && e.trigger.probability !== undefined) ok = rng.next() < e.trigger.probability;
        if (!ok) continue;
        s.fired.add(e.id);
        fired.push(e.id);
        const choice = choices[e.id];
        const opt = choice ? e.options.find((o) => o.id === choice) : undefined;
        if (opt) {
          s.queued.push({ year: k + 1, opt, title: e.title });
          journal.push(`Événement : ${e.title} → ${opt.label}`);
        } else journal.push(`Événement : ${e.title} (en attente de décision)`);
      }
    }

    years.push({ year, kpis, service_index: { ...s.svc }, account: { resources, expenses, balance_state: stateBal, balance_asso: asso, balance_apul: apul, balance_apu: apuBal }, events_fired: fired, journal });
  }
  return { base_year: baseYear, years, game_over: gameOver };
}

/* ----------------------------------------------------------------------------- API */
const YEARS = (ctx: EngineContext) => Math.max(1, ctx.baseline.asso.trend.length);

export const mockEngine: EngineApi = {
  name: "MOCK — moteur de développement",
  isMock: true,
  caps: { kpis: new Set(), priceEffects: false, attribution: false, variants: false, missionCuts: false, verdictRules: false },
  simulate: (ctx, programme, financing, choices, seed) => simulateCore(ctx, programme, financing, choices, seed, { withEvents: true, years: YEARS(ctx) }),
  trend: (ctx, seed) => simulateCore(ctx, { measure_ids: [], hyp: {} }, { revenue_raises: {}, across_the_board_cut: 0 }, {}, seed, { withEvents: false, years: YEARS(ctx) }),
  priceEffects: () => null,
  attribute2032: () => null,
  // Bilan chiffré (ADR 0015) : réservé au moteur réel ; le résultat retombe sur les chiffres clés.
  scorecard: () => null,
  explainScorecard: () => null,
  needGap: (ctx, programme) =>
    programme.measure_ids.reduce((a, id) => {
      const m = ctx.measures.find((x) => x.id === id);
      return m ? a + regimeCost(m, programme.hyp) : a;
    }, 0),
  previewFinancing: (ctx, programme, financing, seed): FinancingPreview => {
    const b = ctx.baseline;
    const tl = simulateCore(ctx, programme, financing, {}, seed, { withEvents: false, years: 1 });
    const y = tl.years[0]!;
    const gap = mockEngine.needGap(ctx, programme);
    const fin = financingEffects(ctx, financing);
    const gdp0 = B(b, "macro", "gdp_nominal");
    const balance0 = figVal(b.state.balance, "state.balance");
    const asso0 = figVal(b.asso.trend[0] ?? b.asso.balance, "asso.trend");
    const apul0 = figVal(b.apul.trend[0] ?? b.apul.balance, "apul.trend");
    const staticBal = balance0 - gap + fin.stat + asso0 + apul0;
    const gdp1 = gdp0 * (1 + (B(b, "macro", "potential_growth") + B(b, "macro", "inflation")) / 100);
    const debt0 = B(b, "apu", "debt_bn");
    return {
      gap_bn: gap,
      raised_static_bn: fin.stat,
      raised_after_reaction_bn: fin.raw,
      debt_residual_bn: Math.max(0, gap - fin.raw),
      deficit_static_pct: (staticBal / gdp0) * 100,
      deficit_simulated_pct: y.kpis.deficit_pct_gdp!,
      debt_static_pct: ((debt0 - staticBal) / gdp1) * 100,
      debt_simulated_pct: y.kpis.debt_pct_gdp!,
      political_cost_per_year: Math.round(fin.pol),
      political_cost_programme: Math.round(programmePoliticalCost(ctx, programme)),
      rabot_bn_per_pt: P(ctx.params, "rabot_bn_per_pct"),
      first_year: y,
    };
  },
  measureTrajectory: (ctx, measureId, hyp): TrajectoryPoint[] => {
    const none: Financing = { revenue_raises: {}, across_the_board_cut: 0 };
    const n = YEARS(ctx);
    const base = simulateCore(ctx, { measure_ids: [], hyp: {} }, none, {}, 1, { withEvents: false, years: n }).years;
    const withM = simulateCore(ctx, { measure_ids: [measureId], hyp }, none, {}, 1, { withEvents: false, years: n }).years;
    return withM.map((y, i) => {
      const b0 = base[i]!;
      return {
        year: y.year,
        d_balance_apu_bn: y.account.balance_apu - b0.account.balance_apu,
        d_growth_pt: y.kpis.growth_pct! - b0.kpis.growth_pct!,
        d_unemployment_pt: y.kpis.unemployment_pct! - b0.kpis.unemployment_pct!,
        d_trade_bn: y.kpis.trade_balance_bn! - b0.kpis.trade_balance_bn!,
        d_debt_pct_gdp: y.kpis.debt_pct_gdp! - b0.kpis.debt_pct_gdp!,
        d_deficit_pct_gdp: y.kpis.deficit_pct_gdp! - b0.kpis.deficit_pct_gdp!,
        d_poverty_pt: y.kpis.poverty_pct! - b0.kpis.poverty_pct!,
        d_cohesion: y.kpis.cohesion! - b0.kpis.cohesion!,
        d_co2_mt: y.kpis.co2_mt! - b0.kpis.co2_mt!,
      };
    });
  },
  projectRemaining: (ctx, programme, financing, choices, seed, from) =>
    simulateCore(ctx, programme, financing, choices, seed, { withEvents: true, years: YEARS(ctx) }).years.slice(from),
  verdict: (ctx, tl): Verdict => {
    const b = ctx.baseline;
    const p = ctx.params;
    const h = tl.years[tl.years.length - 1];
    if (!h) return { score: 0, score_max: 0, reelected: false, headline: "Mandat interrompu.", lines: [], flags: [], rules: [] };
    const k0 = {
      debt: B(b, "apu", "debt_pct_gdp"),
      deficit: B(b, "apu", "deficit_pct_gdp"),
      unemp: B(b, "macro", "unemployment_rate"),
      pov: B(b, "macro", "poverty_rate"),
      co2: B(b, "macro", "ghg_emissions"),
      trade: B(b, "trade", "balance_bn"),
      eu: B(b, "politics", "eu_relation"),
    };
    const k = h.kpis;
    const part = (w: string, x: number) => P(p, w) * clamp(x, 0, 1);
    const score = Math.round(
      part("score_w_debt", (k0.debt - k.debt_pct_gdp! + P(p, "score_debt_offset")) / P(p, "score_debt_range")) +
        part("score_w_unemp", (k0.unemp - k.unemployment_pct! + P(p, "score_unemp_offset")) / P(p, "score_unemp_range")) +
        part("score_w_poverty", (k0.pov - k.poverty_pct! + P(p, "score_poverty_offset")) / P(p, "score_poverty_range")) +
        part("score_w_cohesion", k.cohesion! / 100) +
        part("score_w_confidence", k.confidence! / 100),
    );
    const scoreMax = ["score_w_debt", "score_w_unemp", "score_w_poverty", "score_w_cohesion", "score_w_confidence"].reduce((a, id) => a + P(p, id), 0);
    const reelected = k.political_capital! > P(p, "reelection_capital_min") && k.cohesion! > P(p, "reelection_cohesion_min");
    return {
      score,
      score_max: scoreMax,
      reelected,
      headline: reelected ? "Réélu." : "Battu.",
      lines: [
        { label: "Dette / PIB", from: k0.debt, to: k.debt_pct_gdp!, unit: "%", dec: 0 },
        { label: "Déficit public / PIB", from: k0.deficit, to: k.deficit_pct_gdp!, unit: "%", dec: 1 },
        { label: "Chômage", from: k0.unemp, to: k.unemployment_pct!, unit: "%", dec: 1 },
        { label: "Pauvreté", from: k0.pov, to: k.poverty_pct!, unit: "%", dec: 1 },
        { label: "Émissions de CO₂", from: k0.co2, to: k.co2_mt!, unit: "Mt", dec: 0 },
        { label: "Balance commerciale", from: k0.trade, to: k.trade_balance_bn!, unit: "Md€", dec: 0 },
        { label: "Relation UE", from: k0.eu, to: k.eu_relation!, unit: "", dec: 0 },
      ],
      flags: [],
      // Règles du mock (capital et cohésion seulement), seuils des fixtures.
      rules: [
        { id: "capital", label: "Capital politique", value: k.political_capital!, threshold: P(p, "reelection_capital_min"), ok: k.political_capital! > P(p, "reelection_capital_min"), kind: "min", derived: true },
        { id: "cohesion", label: "Cohésion sociale", value: k.cohesion!, threshold: P(p, "reelection_cohesion_min"), ok: k.cohesion! > P(p, "reelection_cohesion_min"), kind: "min", derived: true },
      ],
    };
  },
};
